import * as React from 'react'
import {
  useDataProvider,
  useNotify,
  useRefresh,
  fetchStart,
  fetchEnd,
} from 'react-admin'
import { useDispatch } from 'react-redux'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import EventRepeatIcon from '@material-ui/icons/Autorenew'
import {
  extendResultMessage,
  extendSeries,
  extendTarget,
  formatDay,
  isExtendable,
  recurringErrorMessage,
} from './recurringSeries'

// "Reeks verlengen" (ADR 0015): een Administrator mag een actieve wekelijkse,
// tweewekelijkse of maandelijkse reeks verlengen tot laatste datum + 3 maanden.
const ExtendSeriesButton = ({ record, className, httpClient }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const dispatch = useDispatch()
  const [open, setOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [lastDate, setLastDate] = React.useState(null)

  const extendable = isExtendable(record)
  const eventId = record && record.id
  const endDate = record && record.endDate

  // laatste datum van de reeks; opnieuw ophalen na verlengen (endDate wijzigt)
  React.useEffect(() => {
    if (!extendable) return undefined
    let active = true
    dataProvider
      .getList('date', {
        filter: { eventID: eventId },
        sort: { field: 'startDateTime', order: 'DESC' },
        pagination: { page: 1, perPage: 1 },
      })
      .then(({ data }) => {
        if (active) setLastDate((data && data[0]) || null)
      })
      .catch(() => {
        if (active) setLastDate(null)
      })
    return () => {
      active = false
    }
  }, [dataProvider, extendable, eventId, endDate])

  if (!extendable) return null

  const target = lastDate ? extendTarget(lastDate.startDateTime) : null
  const label = target
    ? `Verlengen tot ${formatDay(target)}`
    : 'Reeks verlengen'

  const handleExtend = () => {
    setLoading(true)
    dispatch(fetchStart())
    extendSeries({ eventId, httpClient })
      .then((result) => {
        notify(extendResultMessage(result))
        refresh()
      })
      .catch((error) => {
        notify(recurringErrorMessage(error), 'warning')
      })
      .finally(() => {
        setLoading(false)
        dispatch(fetchEnd())
        setOpen(false)
      })
  }

  return (
    <>
      <Button
        color="primary"
        size="small"
        variant="outlined"
        className={className}
        onClick={() => setOpen(true)}
        endIcon={<EventRepeatIcon />}>
        Reeks verlengen
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="extend-series-title">
        <DialogTitle id="extend-series-title">Reeks verlengen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {lastDate
              ? `De laatste datum is ${formatDay(
                  lastDate.startDateTime
                )}. De reeks wordt verlengd t/m ${formatDay(
                  target
                )} (laatste datum + 3 maanden), met de tijden van de laatste datum.`
              : 'De reeks wordt verlengd tot 3 maanden na de laatste datum.'}{' '}
            De organisator wordt aangemeld voor de nieuwe data; groepsleden
            krijgen geen nieuwe uitnodiging.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Annuleren
          </Button>
          <Button
            onClick={handleExtend}
            color="primary"
            variant="contained"
            disabled={loading}>
            {label}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ExtendSeriesButton
